import { AlertCircle, CheckCircle2, CircleDashed, Loader } from 'lucide-react';
import type { JSX } from 'react';

import { StatTile } from '@/components/ui/stat-tile';
import type { ProgressSummaryView } from '@/features/memorization/types';

function formatScore(score: number | null): string {
  return score === null ? '-' : score.toFixed(1);
}

/**
 * The stat-tile grid shared by every Progress read - individual
 * (`ProgressSection`, Phase G-1) and cross-student aggregate (Class/
 * Learning Space/Organization, Phase G-6) alike. Renders the backend's
 * own authoritative numbers verbatim; no metric is computed client-side.
 */
export function ProgressStatsGrid({
  data,
  assignedLabel = 'Ditugaskan',
}: {
  readonly data: ProgressSummaryView;
  /**
   * What `targetsAssignedCount` is called. "Ditugaskan" is right wherever
   * an ustadz did the assigning - including every cross-student aggregate
   * - and wrong for a solo learner reading their own space, who assigned
   * it to themselves. Same reasoning as `TargetList`'s status label, and
   * kept in step with it deliberately: the badge on a target and the tile
   * counting it must never use different words for the same state.
   */
  readonly assignedLabel?: string;
}): JSX.Element {
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label={assignedLabel} value={String(data.targetsAssignedCount)} icon={CircleDashed} />
        <StatTile
          label="Sedang Berjalan"
          value={String(data.targetsInProgressCount)}
          icon={Loader}
        />
        <StatTile
          label="Perlu Perbaikan"
          value={String(data.targetsNeedsRevisionCount)}
          icon={AlertCircle}
        />
        <StatTile
          label="Selesai"
          value={String(data.targetsCompletedCount)}
          icon={CheckCircle2}
          celebrate={data.targetsCompletedCount > 0}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Skor Akurasi" value={formatScore(data.averageScoreAccuracy)} />
        <StatTile label="Skor Kelancaran" value={formatScore(data.averageScoreFluency)} />
        <StatTile label="Skor Tajwid" value={formatScore(data.averageScoreTajwid)} />
        <StatTile label="Skor Kepercayaan Diri" value={formatScore(data.averageScoreConfidence)} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatTile label="Murajaah Selesai" value={String(data.murajaahCompletedCount)} />
        <StatTile label="Murajaah Terlewat" value={String(data.murajaahMissedCount)} />
      </div>
    </>
  );
}
